import clsx, { ClassValue } from 'clsx';
import { CSSProperties, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import styles from './Dialog.module.css';

export interface DialogProps
  extends Omit<HTMLAttributes<HTMLDialogElement>, 'className' | 'title'> {
  children?: ReactNode;
  className?: ClassValue;
  style?: CSSProperties;
  isOpen?: boolean;
  lightDismiss?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onClickOutside?: () => void;
  /**
   * How the dialog enters the stack. Mutually exclusive:
   *
   * - `'standard'` (default): a non-modal `dialog.show()` rendered in normal
   *   flow and stacked by z-index. The page behind is inerted, but other
   *   top-layer UI (toasts, menus) and sibling modals stay interactive. Raises
   *   to the front on click. The standard modal — used by card modals and most
   *   surfaces.
   * - `'modal'`: `showModal()`, which enters the top layer and makes
   *   *everything* outside the dialog inert, including toasts and other
   *   top-layer UI. Typically just alerts and confirms.
   * - `'floating'`: like `'standard'` but does **not** inert the background, so
   *   the whole page stays live. No backdrop; raises to the front on click so
   *   it can sit above other modals. For movable surfaces like the Ask panel.
   */
  mode?: 'standard' | 'modal' | 'floating';
  /**
   * Page-width sizing: the dialog takes the page width
   * (`--modal-page-min-width`, default 100Q; override per-modal via a
   * className) and expands to the full viewport width when `fullWidth` is set.
   * Height still fits the content — use this for a page-width modal that should
   * not be full height.
   */
  pageWidth?: boolean;
  /**
   * Full-page modal: `pageWidth` plus full viewport height, and a full-screen
   * layout on narrow (mobile) viewports.
   */
  fullPage?: boolean;
  /** With `pageWidth` / `fullPage`, grow the dialog to the full viewport width.
   */
  fullWidth?: boolean;
  /**
   * Full-screen modal: edge-to-edge (100dvw × 100dvh) with no border, radius,
   * shadow, or margin. For immersive surfaces like the image lightbox. Style
   * the background/chrome via a `className`.
   */
  fullScreen?: boolean;
  /**
   * Animate the open/close transition and stay mounted until the exit animation
   * finishes. Without it the dialog unmounts immediately on close, so a modal
   * with no close transition can't linger after its backdrop is gone. Pass it
   * for a modal that animates on its own, as alerts/confirms do; nested modals
   * animate regardless.
   */
  animate?: boolean;
  testID?: string;
}

export function Dialog({
  children,
  className,
  style,
  isOpen,
  onOpenChange,
  onClickOutside,
  lightDismiss = false,
  mode = 'standard',
  pageWidth = false,
  fullPage = false,
  fullWidth = false,
  fullScreen = false,
  animate = false,
  testID,
  ...rest
}: DialogProps) {
  const isStandard = mode === 'standard';
  const isModal = mode === 'modal';
  const isFloating = mode === 'floating';
  const ref = useRef<HTMLDialogElement>(null);
  const [isMounted, setIsMounted] = useState(!!isOpen);
  const [animateWidth, setAnimateWidth] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  const onClickOutsideRef = useRef(onClickOutside);

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
    onClickOutsideRef.current = onClickOutside;
  });

  // Mount as soon as the modal opens
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  // Enable the width transition only after the modal has mounted, so it doesn't
  // animate its width on open
  useEffect(() => {
    if (!fullPage && !pageWidth) {
      return;
    }
    const { signal, abort } = getController();
    setControlledTimeout(() => setAnimateWidth(true), 100, signal);
    return abort;
  }, [fullPage, pageWidth]);

  // Keep the dialog in the DOM until the close transition ends
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted) {
      return;
    }

    const { signal, abort } = getController();
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== dialog) {
        return;
      }
      if (!dialog.open) {
        setIsMounted(false);
      }
    };
    dialog.addEventListener('transitionend', handleTransitionEnd, { signal });
    return abort;
  }, [isMounted]);

  // Route dismissals (Escape, close()) through onOpenChange
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted) {
      return;
    }

    const { signal, abort } = getController();
    const requestClose = () => onOpenChangeRef.current?.(false);

    if (isModal) {
      // showModal(): Escape fires `cancel`; route it and any close() through.
      const handleCancel = (event: Event) => {
        event.preventDefault();
        requestClose();
      };
      dialog.addEventListener('cancel', handleCancel, { signal });
      dialog.addEventListener('close', requestClose, { signal });
      return abort;
    }

    // standard + floating: a non-modal dialog.show() gets no `cancel`, so close on
    // Escape manually when this dialog should handle it (see shouldCloseOnEscape).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldCloseOnEscape(dialog, event)) {
        requestClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, { signal });
    return abort;
  }, [isMounted, isModal]);

  // Notify clicks outside modal dialogs and light-dismiss
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isModal) {
      return;
    }
    const { signal, abort } = getController();
    const wasDismissingAutoPopover = trackAutoPopoverDismiss(signal);
    // Clicks on a modal dialog's ::backdrop target the dialog element itself
    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog && !wasDismissingAutoPopover()) {
        onClickOutsideRef.current?.();
        if (lightDismiss) {
          dialog.close();
        }
      }
    };
    dialog.addEventListener('click', handleClick, { signal });
    return abort;
  }, [isMounted, isModal, lightDismiss]);

  // Notify clicks outside standard modal dialogs and light-dismiss. Floating
  // surfaces opt out — they never dismiss on an outside click, so the page
  // behind stays live.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isStandard) {
      return;
    }
    const { signal, abort } = getController();
    const wasDismissingAutoPopover = trackAutoPopoverDismiss(signal);
    // Outside clicks land on the shared backdrop or the inerted page behind the
    // modal, so detect them at the document level.
    const handleClick = (event: MouseEvent) => {
      if (wasDismissingAutoPopover()) {
        return;
      }
      if (isOutsideModalClick(dialog, event)) {
        onClickOutsideRef.current?.();
        if (lightDismiss) {
          dialog.close();
        }
      }
    };
    // delay adding this listener until after the dialog is opened, since the
    // click that opened the dialog can still be bubbling to the document
    setControlledTimeout(
      () => document.addEventListener('click', handleClick, { signal }),
      100,
      signal,
    );
    return abort;
  }, [isMounted, isStandard, lightDismiss]);

  // Sync the isOpen prop to the dialog's actual shown state
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !isMounted || !isOpen) {
      return;
    }

    if (isModal) {
      // Native modal: top layer, inert background and backdrop all from the UA.
      if (!dialog.open) {
        dialog.showModal();
      }
      return () => {
        if (dialog.open) {
          dialog.close();
        }
      };
    }

    // standard + floating: a non-modal dialog rendered in normal flow, then move
    // focus in. `standard` modals share one z-index and stack by DOM (open) order;
    // they inert the page behind them. `floating` sits above the whole band via a
    // fixed z-index (see Dialog.module.css) and leaves the page live.
    const previousFocus = document.activeElement;
    if (!dialog.open) {
      dialog.show();
    }
    (dialog.querySelector<HTMLElement>('[autofocus]') ?? dialog).focus();
    const inertedElements = isStandard ? inertBackground() : [];

    return () => {
      for (const element of inertedElements) {
        element.inert = false;
      }
      if (dialog.open) {
        dialog.close();
      }
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    };
  }, [isMounted, isOpen, isModal, isStandard]);

  // Non-animated dialogs unmount immediately on close; animated ones stay for
  // the exit transition.
  const shouldCloseImmediately = !isOpen && !animate;
  if (!isMounted || shouldCloseImmediately) {
    return null;
  }

  // Portal every dialog into <body> so all open modals are DOM siblings. `modal`
  // uses showModal() (top layer); `standard`/`floating` render in normal flow and
  // stack by z-index (see Dialog.module.css). tabIndex lets a click on the
  // surface's own chrome focus it so keyboard focus lands inside on open.
  return createPortal(
    <dialog
      ref={ref}
      aria-modal={isStandard ? true : undefined}
      tabIndex={isModal ? undefined : -1}
      data-floating={isFloating || undefined}
      data-modal-animate={animate || undefined}
      className={clsx(
        styles.dialog,
        (pageWidth || fullPage) && styles.pageWidth,
        fullPage && styles.fullPage,
        fullScreen && styles.fullScreen,
        fullWidth && styles.fullWidth,
        (pageWidth || fullPage) && animateWidth && styles.animateWidth,
        className,
      )}
      style={style}
      data-testid={testID}
      {...rest}
    >
      {children}
    </dialog>,
    document.body,
  );
}

const OPEN_AUTO_POPOVER_SELECTOR = '[popover]:not([popover="manual" i]):popover-open';

function shouldCloseOnEscape(dialog: HTMLDialogElement, event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented || !dialog.open) {
    return false;
  }
  // A native modal dialog (showModal alert/confirm) above closes via its own
  // cancel event.
  if (safeQuerySelector('dialog:modal')) {
    return false;
  }
  // An open auto popover (menu, select) light-dismisses on this Escape
  if (safeQuerySelector(OPEN_AUTO_POPOVER_SELECTOR)) {
    return false;
  }
  // Escape closes the last-opened non-modal dialog in DOM order — a standard modal
  // or the floating Ask panel, whichever was opened most recently.
  return isLastOpenDialog(dialog, 'dialog[open]:not(:modal)');
}

/**
 * The browser light-dismisses an open auto popover (anchored sheet, menu)
 * during pointerdown — before the resulting click reaches our listeners — so
 * by click time the popover is gone from both the DOM and the event's
 * composedPath, and its dismissing click would read as a click outside the
 * dialog. Records whether an auto popover was open at pointerdown; the
 * returned function reports (and resets) that flag so such clicks can be
 * ignored. The click itself still propagates normally, keeping the rest of
 * the page live (e.g. switching between toolbar menus with one click).
 */
function trackAutoPopoverDismiss(signal: AbortSignal): () => boolean {
  let isDismissingAutoPopover = false;
  document.addEventListener(
    'pointerdown',
    () => {
      isDismissingAutoPopover = !!safeQuerySelector(OPEN_AUTO_POPOVER_SELECTOR);
    },
    { signal, capture: true },
  );
  return () => {
    const wasDismissing = isDismissingAutoPopover;
    isDismissingAutoPopover = false;
    return wasDismissing;
  };
}

function isOutsideModalClick(dialog: HTMLDialogElement, event: MouseEvent): boolean {
  // Synthetic clicks (element.click(), e.g. the hidden file-picker input
  // appended to <body>) are not user interactions and must not light-dismiss
  if (!event.isTrusted) {
    return false;
  }
  if (!dialog.open || !isTopmostModal(dialog)) {
    return false;
  }
  // A click can re-render and detach the clicked element (e.g. the modal resize
  // button whose icon swaps on click) before the event finishes bubbling to the
  // document, making contains()/closest() checks read as outside the dialog. So
  // Use the event's dispatch-time path, not event.target's current ancestry.
  const path = event.composedPath();
  if (path.includes(dialog)) {
    return false;
  }
  // Clicks in top-layer UI above the modal (menus, toasts, tooltips, alerts) or
  // in another dialog aren't outside clicks
  if (path.some(isTopLayerElement)) {
    return false;
  }
  return true;
}

function isTopLayerElement(target: EventTarget): boolean {
  return (
    target instanceof Element && (target.tagName === 'DIALOG' || target.hasAttribute('popover'))
  );
}

/**
 * showModal() makes everything outside the dialog inert natively; `standard`-mode
 * surfaces (non-modal dialog.show()) apply the equivalent by hand, leaving
 * top-layer elements (dialogs and popovers such as toasts and menus) interactive.
 * Only returns elements this call inerted, so an inert attribute set elsewhere is
 * left alone on cleanup.
 */
function inertBackground(): HTMLElement[] {
  const inerted: HTMLElement[] = [];
  for (const child of document.body.children) {
    if (child instanceof HTMLElement && !child.matches('dialog, [popover]') && !child.inert) {
      child.inert = true;
      inerted.push(child);
    }
  }
  return inerted;
}

// Dialogs portal to <body> in open order, so the last dialog matching
// `selector` is the most recently opened / topmost.
function isLastOpenDialog(dialog: HTMLDialogElement, selector: string): boolean {
  const open = safeQuerySelectorAll<HTMLDialogElement>(selector);
  return open.length === 0 || open[open.length - 1] === dialog;
}

// The topmost standard modal (they share --modal-zindex and stack by DOM
// order). Excludes native showModal dialogs (:modal, in the top layer) and
// floating panels ([data-floating], which sit above the band on their own).
function isTopmostModal(dialog: HTMLDialogElement): boolean {
  return isLastOpenDialog(dialog, 'dialog[open]:not(:modal):not([data-floating])');
}

// Pseudo-class selectors (:modal, :popover-open) throw in some engines (jsdom);
// swallow that and treat it as "no match".
function safeQuerySelector(selector: string): Element | null {
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function safeQuerySelectorAll<T extends Element>(selector: string): T[] {
  try {
    return Array.from(document.querySelectorAll<T>(selector));
  } catch {
    return [];
  }
}
